import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAsync, Table, Loading, ErrorBox, Empty, Field, Modal, Chip, fmtDate, useAction } from "./AdminUI";

const SECTION = { fontSize: 17, marginTop: 20, marginBottom: 6, color: "#064E3B" };

const KV = ({ label, value, testId }) => (
  <>
    <dt>{label}</dt>
    <dd data-testid={testId}>{value}</dd>
  </>
);

const STATUS_LABEL = {
  approved: "Approved / Live", pending: "Pending Approval", rejected: "Rejected",
  correction_requested: "Correction Requested", incomplete: "Incomplete", submitted: "Pending Approval",
};

const ProfileDetailModal = ({ profileId, onClose, onDone }) => {
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/profiles/${profileId}`), [profileId]);
  const { busy, err, run } = useAction();
  const [note, setNote] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delWord, setDelWord] = useState("");

  if (loading) return <Modal title={profileId} onClose={onClose} wide><Loading /></Modal>;
  const p = data?.profile;
  const d = p?.data || {};
  const pay = data?.payment;

  // ONE current workflow status. Photo moderation is shown separately under Photos/Media
  // so contradictory badges (e.g. Rejected + approved photo) never appear side by side.
  const currentStatus = p.approved && p.status === "submitted" ? "approved" : p.status === "submitted" ? "pending" : p.status;

  const show = (val) => {
    if (val === null || val === undefined) return "Not provided";
    if (Array.isArray(val)) return val.length ? val.join(", ") : "Not provided";
    const s = String(val);
    return s.trim() === "" ? "Not provided" : s;
  };
  // Combo fields with a companion "Other" text input: prefer the member's typed value.
  const combo = (val, other) => {
    const main = show(val);
    if (main !== "Not provided" && String(val).trim().toLowerCase() === "other") {
      const typed = show(other);
      return typed === "Not provided" ? "Other (not specified)" : typed;
    }
    return main;
  };

  const moderate = (action, reqNote = false) =>
    run(
      () => api.post(`/admin/profiles/${profileId}/moderate`, { action, note }),
      () => { toast.success(`Profile ${action.replace("_", " ")} done.`); setNote(""); onDone(); reload(); },
    );

  const doDelete = () =>
    run(
      () => api.post(`/admin/profiles/${profileId}/delete`, {}),
      () => { setConfirmDelete(false); toast.success("Profile permanently deleted."); onDone(); onClose(); },
    );

  const photos = [d.mainPhotoUrl, ...(d.additionalPhotos || [])].filter(Boolean);
  const verifications = data?.verifications || [];

  return (
    <>
      <Modal title={`Profile ${profileId}`} onClose={onClose} wide>
      {error && <ErrorBox error={error} />}
      {!confirmDelete && err && <ErrorBox error={err} />}
      {p && (
        <>
          {/* Summary — the single current status lives here */}
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }} data-testid="ap-profile-detail">
            {photos[0] && <img src={photos[0]} alt="Main profile" className="ap-photo-thumb" style={{ width: 96, height: 96 }} />}
            <dl className="ap-kv" style={{ flex: 1, minWidth: 260 }}>
              <KV label="Current Status" value={<Chip value={currentStatus}>{STATUS_LABEL[currentStatus] || currentStatus}</Chip>} testId="ap-profile-status" />
              <KV label="Profile For" value={show(d.profileFor)} />
              <KV label="Registered" value={fmtDate(p.createdAt)} />
              <KV label="Last Updated" value={fmtDate(p.updatedAt)} />
            </dl>
          </div>

          <h4 className="ap-card-title" style={SECTION}>1. Basic Information</h4>
          <dl className="ap-kv">
            <KV label="Profile For" value={show(d.profileFor)} />
            <KV label="Full Name" value={show(d.fullName)} />
            <KV label="Gender" value={show(d.gender)} />
            <KV label="Date of Birth" value={d.dob ? `${d.dob.day}/${d.dob.month}/${d.dob.year}` : "Not provided"} />
            <KV label="Calculated Age" value={p.age ?? "Not provided"} />
            <KV label="Country" value={show(d.country)} />
            <KV label="State / Province" value={show(d.state)} />
            <KV label="City" value={show(d.city)} />
            <KV label="Nationality" value={show(d.nationality)} />
            <KV label="Community / Jamaat" value={combo(d.communityJamaat, d.communityJamaatOther)} />
            <KV label="Marital Status" value={show(d.maritalStatus)} />
            <KV label="Children" value={show(d.children)} />
            <KV label="About Me / Introduction" value={show(d.intro)} />
          </dl>

          <h4 className="ap-card-title" style={SECTION}>2. Education &amp; Career</h4>
          <dl className="ap-kv">
            <KV label="School Education" value={combo(d.schoolEducation, d.schoolEducationOther)} />
            <KV label="Madarsa Education" value={combo(d.madarsaEducation, d.madarsaEducationOther)} />
            <KV label="Profession" value={combo(d.profession, d.professionOther)} />
            <KV label="Approx Income" value={show(d.income)} />
          </dl>

          <h4 className="ap-card-title" style={SECTION}>3. Religious Information</h4>
          <dl className="ap-kv">
            <KV label="Religious Background" value={show(d.religiousBackgrounds)} />
            <KV label="School of Thought" value={show(d.schoolOfThought)} />
            <KV label="Religious Practice" value={combo(d.religiousPractice, d.religiousPracticeOther)} />
          </dl>

          <h4 className="ap-card-title" style={SECTION}>4. Lifestyle</h4>
          <dl className="ap-kv">
            <KV label="Height" value={show(d.height)} />
            <KV label="Weight" value={show(d.weight)} />
            <KV label="Complexion / Skin Tone" value={combo(d.complexion, d.complexionOther)} />
            <KV label="Diet" value={show(d.diet)} />
            <KV label="Smoking" value={show(d.smoking)} />
            <KV label="Tobacco" value={show(d.tobacco)} />
            <KV label="Alcohol" value={show(d.alcohol)} />
            <KV label="Languages" value={show(d.languages)} />
            <KV label="Hobbies & Interests" value={show(d.hobbies)} />
          </dl>

          <h4 className="ap-card-title" style={SECTION}>5. Family Information</h4>
          <dl className="ap-kv">
            <KV label="Family Type" value={show(d.familyType)} />
            <KV label="Family Values" value={show(d.familyValues)} />
            <KV label="Father's Occupation" value={show(d.fatherOccupation)} />
            <KV label="Mother's Occupation" value={show(d.motherOccupation)} />
            <KV label="Siblings" value={show(d.siblings)} />
            <KV label="Family Location" value={show(d.familyLocation)} />
            <KV label="Brief Family Introduction" value={show(d.familyIntro)} />
          </dl>

          <h4 className="ap-card-title" style={SECTION}>6. Marriage Preferences</h4>
          <dl className="ap-kv">
            <KV label="Preferred Age From" value={show(d.prefAgeFrom)} />
            <KV label="Preferred Age To" value={show(d.prefAgeTo)} />
            <KV label="Preferred Country / Location" value={show(d.prefLocations)} />
            <KV label="Preferred Marital Status" value={show(d.prefMaritalStatuses)} />
            <KV label="Preferred Education" value={show(d.prefEducation)} />
            <KV label="Preferred Profession" value={show(d.prefProfession)} />
            <KV label="Religious Preferences" value={show(d.prefReligious)} />
            <KV label="Preferred Height" value={show(d.prefHeight)} />
            <KV label="Children Preference" value={show(d.prefChildren)} />
            <KV label="What I Am Looking For In My Life Partner" value={show(d.partnerVision)} />
          </dl>

          <h4 className="ap-card-title" style={{ ...SECTION, color: "#B3261E" }}>Private Information — Admin Only</h4>
          <dl className="ap-kv" data-testid="ap-profile-private">
            <KV label="Mobile Number" value={show(d.mobile)} />
            <KV label="Email Address" value={show(d.email)} />
            <KV label="WhatsApp" value={show(d.whatsapp)} />
            <KV label="Parent / Wali Name" value={show(d.waliName)} />
            <KV label="Parent / Wali Relationship" value={show(d.waliRelationship)} />
            <KV label="Parent / Wali Mobile Number" value={show(d.waliMobile)} />
            <KV label="Full Current Address" value={show(d.fullAddress)} />
            <KV label="Preferred Contact Method" value={show(d.preferredContact)} />
          </dl>

          <h4 className="ap-card-title" style={SECTION}>Photos / Media</h4>
          <dl className="ap-kv">
            <KV label="Photo Moderation Status" value={<Chip value={p.photoStatus} data-x="photo">{`Photo: ${p.photoStatus}`}</Chip>} />
            <KV label="Photo Privacy" value={show(d.photoPrivacy)} />
          </dl>
          {photos.length === 0 ? <Empty>No photos uploaded.</Empty> : (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }} data-testid="ap-profile-photos">
              {photos.map((ph, i) => (
                <img key={i} src={ph} alt={`Profile photo ${i + 1}`} className="ap-photo-thumb" style={{ width: 88, height: 88 }} />
              ))}
            </div>
          )}

          <h4 className="ap-card-title" style={SECTION}>Payment &amp; Verification</h4>
          <dl className="ap-kv">
            <KV label="Registration Fee" value={pay ? `₹${pay.final_amount ?? pay.amount ?? "—"}` : "No record"} />
            <KV label="Payment Status" value={pay ? <Chip value={pay.status} /> : "No record"} testId="ap-profile-fee-status" />
            <KV label="Payment Method / Reference" value={pay ? [pay.method, pay.reference].filter(Boolean).join(" · ") || "Not provided" : "Not provided"} />
            {pay?.waiver_reason && <KV label="Waiver Reason" value={`${pay.waiver_reason}${pay.waived_by ? ` (by ${pay.waived_by})` : ""}`} />}
            <KV label="Verification Status" value={p.verified ? <Chip value="verified">Verified</Chip> : <Chip value="not_verified">Not verified</Chip>} testId="ap-profile-verified" />
          </dl>
          {verifications.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {verifications.map((vrec) => (
                <div key={vrec.id} className="ap-msg">
                  <div className="ap-msg-meta">{vrec.type} · <Chip value={vrec.status} /> · {vrec.adminEmail} · {fmtDate(vrec.createdAt)}</div>
                  {vrec.notes || "No notes"}
                </div>
              ))}
            </div>
          )}

          <h4 className="ap-card-title" style={SECTION}>Admin Review</h4>
          {(p.rejectionReason || p.correctionNote) && (
            <dl className="ap-kv" style={{ marginBottom: 10 }}>
              {p.rejectionReason && <KV label="Rejection Reason" value={p.rejectionReason} />}
              {p.correctionNote && <KV label="Correction Requested" value={p.correctionNote} />}
            </dl>
          )}

          <h4 className="ap-card-title" style={{ fontSize: 15, marginTop: 10 }}>Internal Admin Notes</h4>
          {p.adminNotes.length === 0 ? <Empty>No internal notes.</Empty> : p.adminNotes.map((n, i) => (
            <div key={i} className="ap-msg"><div className="ap-msg-meta">{n.adminEmail} · {fmtDate(n.createdAt)}</div>{n.note}</div>
          ))}

          <div className="ap-row" style={{ marginTop: 14 }}>
            <Field label="Note — internal note / rejection reason">
              <input className="ap-input" data-testid="ap-profile-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Required for reject / correction; optional internal note" />
            </Field>
          </div>
          {err && <ErrorBox error={err} />}
          <div className="ap-table-actions">
            <button type="button" className="btn btn-solid btn-sm" data-testid="ap-profile-approve" disabled={busy} onClick={() => moderate("approve")}>Approve</button>
            <button type="button" className="btn btn-outline btn-sm" data-testid="ap-profile-reject" disabled={busy || !note} onClick={() => moderate("reject")}>Reject</button>
            <button type="button" className="btn btn-outline btn-sm" data-testid="ap-profile-correct" disabled={busy || !note} onClick={() => moderate("request_correction")}>Request Correction</button>
            <button type="button" className="btn btn-outline btn-sm" data-testid="ap-profile-hide" disabled={busy} onClick={() => moderate(p.approved ? "hide" : "unhide")}>{p.approved ? "Hide from public" : "Publish"}</button>
            <button
              type="button" className="btn btn-outline btn-sm" disabled={busy || !note}
              onClick={() => run(() => api.post(`/admin/profiles/${profileId}/notes`, { note }), () => { setNote(""); toast.success("Note saved."); reload(); })}
            >
              Add Note
            </button>
          </div>
          {p.approved && (
            <p className="ap-note" style={{ marginTop: 8 }}>This profile is live in Find Matches. "Hide from public" unpublishes it without deleting.</p>
          )}
          {!p.approved && p.status === "submitted" && (
            <p className="ap-note" style={{ marginTop: 8 }}>Publishing requires the ₹500 registration fee to be Paid or Fee Waived first — then approve/publish here.</p>
          )}

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px dashed rgba(28,25,23,0.25)" }} data-testid="ap-profile-danger-zone">
            <p className="ap-note" style={{ marginBottom: 10 }}>
              <strong style={{ color: "#B3261E" }}>Danger zone:</strong> Delete Profile permanently removes this
              matrimonial profile from Find Matches, search results and every member-facing listing. This is separate
              from Reject / Hide. The member's login account is kept, and payment records and audit history are preserved.
            </p>
            <button
              type="button" className="btn btn-outline btn-sm" data-testid="ap-profile-delete" disabled={busy}
              style={{ color: "#B3261E", borderColor: "#B3261E" }}
              onClick={() => { setConfirmDelete(true); setDelWord(""); }}
            >
              Delete Profile Permanently
            </button>
          </div>
        </>
      )}
      </Modal>
      {confirmDelete && (
        <Modal title={`Permanently delete ${profileId}?`} onClose={() => setConfirmDelete(false)}>
          <p className="ap-note" data-testid="ap-profile-delete-warning">
            <strong>This matrimonial profile will be permanently removed</strong> from Find Matches,
            search results, recommendations and all member-facing listings. This action cannot be undone.
          </p>
          <ul className="ap-note" style={{ margin: "0 0 12px", paddingLeft: 18 }}>
            <li>The member's login/account is <strong>not</strong> deleted.</li>
            <li>Payment records (₹500) and audit history are preserved.</li>
            <li>Linked interests and match cases involving this profile are removed.</li>
          </ul>
          {err && <ErrorBox error={err} />}
          <Field label="Type DELETE to confirm">
            <input
              className="ap-input" data-testid="ap-profile-delete-confirm-input" value={delWord}
              onChange={(e) => setDelWord(e.target.value)} placeholder="DELETE"
            />
          </Field>
          {delWord.trim() !== "" && delWord.trim() !== "DELETE" && (
            <p className="ap-note" data-testid="ap-profile-delete-hint">
              Please type DELETE in capital letters exactly — the button enables only on an exact match.
            </p>
          )}
          <div className="ap-table-actions">
            <button type="button" className="btn btn-outline btn-sm" data-testid="ap-profile-delete-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button
              type="button" className="btn btn-solid btn-sm" data-testid="ap-profile-delete-confirm-btn"
              disabled={delWord.trim() !== "DELETE" || busy} style={{ background: "#B3261E", borderColor: "#B3261E" }}
              onClick={doDelete}
            >
              {busy ? "Deleting…" : "Delete Permanently"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

const TABS = [
  ["all", "All Profiles"], ["pending", "Pending Approval"], ["approved", "Approved / Live"],
  ["rejected", "Rejected"], ["correction", "Correction Requested"], ["incomplete", "Incomplete"],
];

export const AdminProfiles = () => {
  const [tab, setTab] = useState("all");
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/profiles?tab=${tab}`), [tab]);
  const [viewId, setViewId] = useState(null);

  return (
    <>
      <div className="ap-card">
        <h3 className="ap-card-title">Profile Management</h3>
        <div className="ap-tabs" data-testid="ap-profile-tabs">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" className={`ap-tab${k === tab ? " is-active" : ""}`} data-testid={`ap-profile-tab-${k}`} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : (
          tab === "incomplete" ? (
            data.incompleteUsers.length === 0 ? <Empty>Every registered user has completed a profile.</Empty> : (
              <Table columns={["Name", "Email", "Registered", ""]}>
                {data.incompleteUsers.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.fullName || "—"}</td>
                    <td>{u.email}</td>
                    <td>{fmtDate(u.createdAt)}</td>
                    <td><span className="ap-chip ap-chip-gray">Profile not submitted</span></td>
                  </tr>
                ))}
              </Table>
            )
          ) : data.profiles.length === 0 ? <Empty>No profiles in this view.</Empty> : (
            <Table columns={["Profile", "Name", "Gender", "Age", "Location", "Status", "Photo", "Fee", "Updated", ""]}>
              {data.profiles.map((p) => (
                <tr key={p.profileId}>
                  <td>{p.profileId}{p.verified ? " ✓" : ""}</td>
                  <td>{p.fullName || "—"}</td>
                  <td>{p.gender}</td>
                  <td>{p.age ?? "—"}</td>
                  <td>{[p.city, p.country].filter(Boolean).join(", ")}</td>
                  <td><Chip value={p.approved && p.status === "submitted" ? "approved" : p.status} /></td>
                  <td><Chip value={p.photoStatus} /></td>
                  <td><Chip value={p.paymentStatus || "none"}>{p.paymentStatus || "—"}</Chip></td>
                  <td>{fmtDate(p.updatedAt)}</td>
                  <td><button type="button" className="btn btn-outline btn-sm" data-testid={`ap-profile-view-${p.profileId}`} onClick={() => setViewId(p.profileId)}>Review</button></td>
                </tr>
              ))}
            </Table>
          )
        )}
      </div>
      {viewId && <ProfileDetailModal profileId={viewId} onClose={() => setViewId(null)} onDone={reload} />}
    </>
  );
};

export const AdminPhotos = () => {
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/photos"), []);
  const { busy, err, run } = useAction();
  const [note, setNote] = useState("");

  const act = (profileId, action) =>
    run(
      () => api.post(`/admin/photos/${profileId}/moderate`, { action, note }),
      () => { toast.success(`Photo ${action.replace("_", " ")}.`); setNote(""); reload(); },
    );

  return (
    <div className="ap-card">
      <h3 className="ap-card-title">Photos / Media Review</h3>
      <p className="ap-note" style={{ marginBottom: 14 }}>Rejected or removed photos are immediately hidden from the public site. Broken/unreadable photo records are flagged below and can be removed without touching the rest of the profile.</p>
      {err && <ErrorBox error={err} />}
      <div className="ap-row" style={{ marginBottom: 14 }}>
        <Field label="Note (required for reject / replacement)"><input className="ap-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.photos.length === 0 ? <Empty>No profile photos yet.</Empty> : (
        <Table columns={["Photo", "Profile", "Name", "Status", "Health", "Updated", "Actions"]}>
          {data.photos.map((p) => (
            <tr key={p.profileId}>
              <td>{p.broken ? <span className="ap-chip ap-chip-gray">No image</span> : <img src={p.photo} alt={p.profileId} className="ap-photo-thumb" />}</td>
              <td>{p.profileId}</td>
              <td>{p.fullName || "—"}</td>
              <td><Chip value={p.photoStatus} /></td>
              <td>{p.broken ? <span className="ap-chip ap-chip-red" data-testid={`ap-photo-broken-${p.profileId}`}>Broken / test record</span> : <span className="ap-chip ap-chip-green">OK</span>}</td>
              <td>{fmtDate(p.updatedAt)}</td>
              <td className="ap-table-actions">
                {!p.broken && <button type="button" className="btn btn-solid btn-sm" disabled={busy} onClick={() => act(p.profileId, "approve")}>Approve</button>}
                {!p.broken && <button type="button" className="btn btn-outline btn-sm" disabled={busy || !note} onClick={() => act(p.profileId, "reject")}>Reject</button>}
                {!p.broken && <button type="button" className="btn btn-outline btn-sm" disabled={busy || !note} onClick={() => act(p.profileId, "request_replacement")}>Request Replacement</button>}
                <button type="button" className="btn btn-outline btn-sm" data-testid={`ap-photo-remove-${p.profileId}`} disabled={busy} onClick={() => act(p.profileId, "remove")}>Remove</button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
};

export const AdminVerification = () => {
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/verifications"), []);
  const { busy, err, run } = useAction();
  const [form, setForm] = useState({ email: "", type: "document", status: "pending", notes: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      <div className="ap-card">
        <h3 className="ap-card-title">Record Verification</h3>
        <p className="ap-note" style={{ marginBottom: 14 }}>A user is marked “Verified” only when an admin performs an actual verification action — never automatically.</p>
        {err && <ErrorBox error={err} />}
        <div className="ap-row">
          <Field label="User Email"><input className="ap-input" data-testid="ap-verif-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Verification Type">
            <select className="ap-select" value={form.type} onChange={(e) => set("type", e.target.value)}>
              <option value="document">Document</option>
              <option value="phone">Phone</option>
              <option value="family-interview">Family Interview</option>
              <option value="reference">Reference Check</option>
            </select>
          </Field>
          <Field label="Status">
            <select className="ap-select" data-testid="ap-verif-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </Field>
        </div>
        <Field label="Notes"><input className="ap-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        <button
          type="button" className="btn btn-solid btn-sm" data-testid="ap-verif-save" disabled={busy}
          onClick={() => run(
            async () => {
              const u = await api.get(`/admin/users?search=${encodeURIComponent(form.email)}`);
              const target = u.users.find((x) => x.email === form.email.trim().toLowerCase());
              if (!target) throw new Error("User not found");
              return api.post("/admin/verifications", { ...form, user_id: target.id });
            },
            () => { toast.success("Verification recorded."); setForm({ email: "", type: "document", status: "pending", notes: "" }); reload(); },
          )}
        >
          {busy ? "Saving…" : "Record Verification"}
        </button>
      </div>
      <div className="ap-card">
        <h3 className="ap-card-title">Verification History</h3>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.verifications.length === 0 ? <Empty>No verification records yet.</Empty> : (
          <Table columns={["User", "Type", "Status", "Notes", "Admin", "Date"]}>
            {data.verifications.map((v) => (
              <tr key={v.id}>
                <td>{v.ownerName || v.ownerEmail}</td>
                <td>{v.type}</td>
                <td><Chip value={v.status} /></td>
                <td>{v.notes || "—"}</td>
                <td>{v.adminEmail}</td>
                <td>{fmtDate(v.createdAt)}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </>
  );
};
